import axios from 'axios';
import apiClient from './apiService';
import pdfPackage from 'pdfjs-dist/package.json';

export const MAX_PREVIEW_BYTES = 50 * 1024 * 1024;
const assetRoot = `${process.env.PUBLIC_URL || ''}/pdfjs/${pdfPackage.version}/`;
let library;

export function loadPdfLibrary() {
  if (!library) {
    library = import(/* webpackIgnore: true */ `${assetRoot}pdf.min.js`).then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = `${assetRoot}pdf.worker.min.js`;
      return pdfjs;
    }).catch((error) => { library = null; throw error; });
  }
  return library;
}

export const pdfAssetOptions = {
  cMapUrl: `${assetRoot}cmaps/`, cMapPacked: true,
  standardFontDataUrl: `${assetRoot}standard_fonts/`, wasmUrl: `${assetRoot}wasm/`,
  isEvalSupported: false,
};

export function safeFileUrl(value) {
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) && !url.username && !url.password ? url.href : '';
  } catch { return ''; }
}

export function fileKind(name, url) {
  const extension = String(name || new URL(url).pathname).trim().toLowerCase().split('.').pop();
  if (extension === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) return 'image';
  return 'other';
}

export async function fetchPreviewPdf(url, signal) {
  const config = { responseType: 'arraybuffer', timeout: 90000, signal, maxContentLength: MAX_PREVIEW_BYTES };
  let response;
  try {
    response = await apiClient.get('/file-preview/pdf', { ...config, previewErrorHandled: true, params: { url } });
  } catch (error) {
    // External file hosts may allow direct reading. Never send the PAI token to them.
    if (![400, 404].includes(error.response?.status) || signal.aborted) throw error;
    response = await axios.get(url, { ...config, withCredentials: false });
  }
  if (response.data.byteLength > MAX_PREVIEW_BYTES) throw new Error('This file exceeds the 50 MB preview limit. Please download it.');
  return new Uint8Array(response.data);
}

export function previewError(error) {
  if (error.name === 'PasswordException') return 'This PDF is password protected. Download it to open with a PDF reader.';
  if (error.response?.status === 413) return 'This file exceeds the 50 MB preview limit. Please download it.';
  if (error.response?.status === 404) return 'The file was not found or its link has expired.';
  if ([401, 403].includes(error.response?.status)) return 'The file is not accessible. Refresh your login or request a new file link.';
  if (error.code === 'ECONNABORTED') return 'Preview loading timed out. Retry or download the file.';
  return 'Unable to preview this file. The link may be unavailable, block access, or contain an invalid PDF. Retry or download the file.';
}
